import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shell } from '../../components/layout/Shell';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Briefcase, Users, ChartBar, Plus, Sparkle } from '@phosphor-icons/react';
import { EmployerTour, useEmployerTour } from '../../components/hikari/EmployerTour';

const EmployerDashboard = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const tour = useEmployerTour();

  useEffect(() => {
    // All ingested + own jobs
    api.get('/jobs').then((r) => setJobs(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <Shell>
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="label-eyebrow text-primary mb-2">EMPLOYER CONTROL ROOM</div>
            <h1 className="font-display font-black text-4xl tracking-tighter">Ranked candidates, zero guesswork.</h1>
          </div>
          <Link to="/employer/jobs"><Button className="rounded-none" data-testid="employer-goto-jobs"><Plus size={16} className="mr-2" /> Manage jobs</Button></Link>
          <Link to="/employer/inbox"><Button variant="outline" className="rounded-none" data-testid="employer-goto-inbox">Applications inbox</Button></Link>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={tour.openTour}
            data-testid="employer-take-tour"
          >
            <Sparkle size={16} weight="fill" className="mr-2 text-primary" /> Take the tour
          </Button>
        </div>

        <div className="grid md:grid-cols-3 gap-0 grid-skeleton mb-10">
          <div className="p-6 bg-white">
            <div className="flex items-center gap-2 mb-3">
              <Briefcase size={20} weight="duotone" className="text-primary" />
              <div className="label-eyebrow">JOBS AVAILABLE</div>
            </div>
            <div className="mono font-black text-5xl">{jobs.length}</div>
            <div className="text-xs text-muted-foreground mt-2">Ingested + posted</div>
          </div>
          <div className="p-6 bg-white">
            <div className="flex items-center gap-2 mb-3">
              <Users size={20} weight="duotone" className="text-primary" />
              <div className="label-eyebrow">CANDIDATE POOL</div>
            </div>
            <div className="mono font-black text-5xl">Open</div>
            <div className="text-xs text-muted-foreground mt-2">Candidates appear once they complete assessments</div>
          </div>
          <div className="p-6 bg-white">
            <div className="flex items-center gap-2 mb-3">
              <ChartBar size={20} weight="duotone" className="text-primary" />
              <div className="label-eyebrow">SCORING ENGINE</div>
            </div>
            <div className="mono font-black text-xl">4-LAYER</div>
            <div className="text-xs text-muted-foreground mt-2">Performance · Consistency · Reliability · Artifacts</div>
          </div>
        </div>

        <div className="label-eyebrow text-muted-foreground mb-3">OPEN ROLES</div>
        {loading ? (
          <div className="mono text-sm text-muted-foreground">Loading...</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 grid-skeleton">
            {jobs.slice(0, 9).map((j) => (
              <Link to={`/employer/jobs/${j.id}`} key={j.id} className="p-6 bg-white hover:bg-secondary/50 transition-colors" data-testid={`emp-job-card-${j.id}`}>
                <div className="label-eyebrow text-primary mb-2">{(j.source || 'manual').toUpperCase()}</div>
                <h3 className="font-display font-black text-lg tracking-tight mb-1">{j.title}</h3>
                <div className="mono text-xs text-muted-foreground mb-3">{j.location || '—'} · {j.country}</div>
                <div className="flex flex-wrap gap-1">
                  {(j.required_skills || []).slice(0, 3).map((s) => (
                    <span key={s} className="label-eyebrow bg-secondary px-2 py-0.5">{s}</span>
                  ))}
                </div>
                <div className="mt-4 mono text-xs text-primary">VIEW RANKED CANDIDATES →</div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <EmployerTour open={tour.open} onClose={tour.closeTour} />
    </Shell>
  );
};

export default EmployerDashboard;
